import React, { useEffect, useRef, useId } from 'react'
import { createPortal } from 'react-dom'
import { Sparkles, X, ArrowRight } from 'lucide-react'
import {
  ChangeGroups,
  VersionTag,
  formatReleaseDate,
} from './whatsNew/changeTypes'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * The one-time notice shown after sign-in on a release the user has not seen.
 *
 * Presentational only: it does not decide *whether* to appear. The caller owns
 * the unseen-release check and marks the release seen — both actions
 * (Got it, View full changelog) and the close button come back through
 * `onDismiss` / `onViewChangelog`, so either one can mark it seen.
 *
 * @param {object}   release          { version, date, changes } — same shape as the page.
 * @param {function} onDismiss        Close without navigating. Also fires on Esc and the scrim.
 * @param {function} onViewChangelog  Close and send the user to /whats-new.
 * @param {number}   maxLines         Change lines to show before the "+ N more" note.
 */
export default function WhatsNewPopup({
  release,
  onDismiss,
  onViewChangelog,
  maxLines = 3,
}) {
  const panelRef = useRef(null)
  const primaryRef = useRef(null)
  const titleId = useId()

  // Esc closes, Tab cycles inside the panel, and focus goes back where it was.
  useEffect(() => {
    const previouslyFocused = document.activeElement

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onDismiss?.()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return

      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE))
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    primaryRef.current?.focus()

    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [onDismiss])

  if (!release) return null

  const changes = release.changes || []
  const shown = changes.slice(0, maxLines)
  const remaining = changes.length - shown.length

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="animate-panel-in fixed inset-0 bg-black/40"
          onClick={onDismiss}
          aria-hidden="true"
        />

        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="animate-fade-in relative w-full max-w-[512px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-6 pt-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm">
                <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
              </div>
              <div>
                <p
                  id={titleId}
                  className="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400"
                >
                  New in Xtract
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <VersionTag version={release.version} highlight />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    <time dateTime={release.date}>{formatReleaseDate(release.date)}</time>
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close"
              className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          </div>

          {/* Changes */}
          <div className="flex flex-col gap-4 px-6 py-5">
            <ChangeGroups changes={shown} stagger={40} />
            {remaining > 0 && (
              <p className="m-0 -mt-1 ml-[25px] text-[13px] text-gray-400 dark:text-gray-500">
                + {remaining} more {remaining === 1 ? 'change' : 'changes'} in this release
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-800">
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex h-9 items-center rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Got it
            </button>
            <button
              ref={primaryRef}
              type="button"
              onClick={onViewChangelog}
              className="group inline-flex h-9 items-center gap-1.5 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy-900 hover:shadow-md active:translate-y-0 active:scale-[0.98]"
            >
              View full changelog
              <ArrowRight
                className="h-[15px] w-[15px] transition-transform duration-200 group-hover:translate-x-0.5"
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
